import { useState } from 'react';
import { UserPlus, Play, Trash2, MessageSquare, FileText, KeyRound } from 'lucide-react';
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { toast } from '@/shared/lib/toast';
import AddCollaboratorsModal from '../components/AddCollaboratorsModal';
import SendGroupMessagesModal from '../components/SendGroupMessagesModal';
import UpdateGroupDescriptionModal from '../components/UpdateGroupDescriptionModal';
import UpdateClientPasswordsModal from '../components/UpdateClientPasswordsModal';

interface Operation {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  action: () => void;
}

export default function Operations() {
  const [isAddCollaboratorsModalOpen, setIsAddCollaboratorsModalOpen] = useState(false);
  const [isSendMessagesModalOpen, setIsSendMessagesModalOpen] = useState(false);
  const [isUpdateDescriptionModalOpen, setIsUpdateDescriptionModalOpen] = useState(false);
  const [isUpdatePasswordsModalOpen, setIsUpdatePasswordsModalOpen] = useState(false);

  const [operations, setOperations] = useState<Operation[]>([
    {
      id: 'add-collaborators',
      title: 'Adicionar Colaboradores aos Grupos',
      description:
        'Adiciona um colaborador em todos os grupos de clientes ativos e o promove a administrador',
      icon: UserPlus,
      action: () => setIsAddCollaboratorsModalOpen(true),
    },
    {
      id: 'send-group-messages',
      title: 'Enviar Mensagens para Grupos',
      description: 'Envia uma mensagem para todos os grupos de clientes ativos',
      icon: MessageSquare,
      action: () => setIsSendMessagesModalOpen(true),
    },
    {
      id: 'update-group-description',
      title: 'Editar Descrição dos Grupos',
      description: 'Atualiza a descrição de todos os grupos de clientes ativos',
      icon: FileText,
      action: () => setIsUpdateDescriptionModalOpen(true),
    },
    {
      id: 'update-client-passwords',
      title: 'Atualizar Senhas dos Clientes',
      description: 'Atualiza a senha da dashboard dos clientes baseado nos emails informados',
      icon: KeyRound,
      action: () => setIsUpdatePasswordsModalOpen(true),
    },
  ]);

  const handleDeleteOperation = (operationId: string) => {
    setOperations((prev) => prev.filter((op) => op.id !== operationId));
    toast.success('Sucesso', 'Operação removida da lista');
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Operações</h1>
        <p className="text-muted-foreground mt-1">Ferramentas e operações úteis do sistema</p>
      </div>

      {/* Operations Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {operations.map((operation) => {
          const Icon = operation.icon;
          return (
            <Card key={operation.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{operation.title}</CardTitle>
                    </div>
                  </div>
                </div>
                <CardDescription className="mt-2">{operation.description}</CardDescription>
              </CardHeader>
              <CardFooter className="flex justify-between">
                <Button onClick={operation.action} className="flex items-center space-x-2">
                  <Play className="h-4 w-4" />
                  <span>Executar</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDeleteOperation(operation.id)}
                  className="flex items-center space-x-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Excluir</span>
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {operations.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="p-4 bg-muted rounded-full mb-4">
            <UserPlus className="h-12 w-12 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Nenhuma operação disponível
          </h3>
          <p className="text-sm text-muted-foreground">
            Todas as operações foram removidas da lista
          </p>
        </div>
      )}

      {/* Modals */}
      <AddCollaboratorsModal
        isOpen={isAddCollaboratorsModalOpen}
        onClose={() => setIsAddCollaboratorsModalOpen(false)}
      />
      <SendGroupMessagesModal
        isOpen={isSendMessagesModalOpen}
        onClose={() => setIsSendMessagesModalOpen(false)}
      />
      <UpdateGroupDescriptionModal
        isOpen={isUpdateDescriptionModalOpen}
        onClose={() => setIsUpdateDescriptionModalOpen(false)}
      />
      <UpdateClientPasswordsModal
        isOpen={isUpdatePasswordsModalOpen}
        onClose={() => setIsUpdatePasswordsModalOpen(false)}
      />
    </div>
  );
}
